/**
 * Meta (Facebook) Pixel — HotelsRiver SSOT for standard conversion events.
 *
 * Events: PageView, Search, ViewContent, InitiateCheckout, AddPaymentInfo, Purchase, Lead.
 * Commerce fields (value, currency, content_type, content_ids) are attached when available.
 */

import {
  buildMetaCommerceParams,
  metaCommerceFromHotelProfile,
  metaCommerceFromOrder,
  metaCommerceFromPackageSelection,
  metaCommerceFromSearchContext,
} from "./metaPixelCommerce";

export const META_PIXEL_ID = "1450650903591948";

const SESSION_PREFIX = "hr_meta_pixel_";

function isFbqReady() {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key) {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

function oncePerSession(key, fn) {
  const storageKey = `${SESSION_PREFIX}${key}`;
  if (safeSessionGet(storageKey)) return;
  safeSessionSet(storageKey);
  fn();
}

function trackMetaEvent(eventName, params = {}, { eventId = null } = {}) {
  try {
    if (!isFbqReady()) return;
    const payload = { ...params };
    if (eventId) payload.eventID = eventId;
    window.fbq("track", eventName, Object.keys(payload).length ? payload : undefined);
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.info("[metaPixel]", eventName, payload);
    }
  } catch {
    /* ignore analytics failures */
  }
}

export function trackMetaPageView() {
  trackMetaEvent("PageView");
}

export function trackMetaSearch(context = {}, { requestId = null } = {}) {
  const commerce = metaCommerceFromSearchContext(context);
  const dedupeKey = requestId != null ? `search_${requestId}` : `search_${commerce.search_string || "unknown"}`;
  oncePerSession(dedupeKey, () => {
    trackMetaEvent("Search", commerce);
  });
}

export function trackMetaViewContent(payload = {}) {
  const {
    hotelInfo = null,
    hotelId = null,
    selectedResult = null,
    value = null,
    content_type = null,
    content_ids = null,
    content_name = null,
    dedupeKey = null,
  } = payload;
  let commerce = {};
  if (hotelInfo || hotelId) {
    commerce = metaCommerceFromHotelProfile(hotelInfo, { hotelId, value });
  } else if (selectedResult) {
    commerce = metaCommerceFromPackageSelection(selectedResult, { value });
  } else {
    commerce = buildMetaCommerceParams({
      value,
      currency: "ILS",
      content_type: content_type || "product",
      content_ids: content_ids || [],
      content_name,
    });
  }
  const key =
    dedupeKey ||
    commerce.content_ids?.join("_") ||
    content_name ||
    "view_unknown";
  oncePerSession(`view_${key}`, () => {
    trackMetaEvent("ViewContent", commerce);
  });
}

export function trackMetaInitiateCheckout(order, orderId = null) {
  const resolvedId = orderId ?? order?.id ?? order?.order_id ?? null;
  if (!resolvedId) return;
  oncePerSession(`checkout_${resolvedId}`, () => {
    trackMetaEvent("InitiateCheckout", metaCommerceFromOrder(order));
  });
}

export function trackMetaAddPaymentInfo(order, orderId = null, extra = {}) {
  const resolvedId = orderId ?? order?.id ?? order?.order_id ?? null;
  const key = resolvedId ? `payment_info_${resolvedId}` : `payment_info_${Date.now()}`;
  oncePerSession(key, () => {
    trackMetaEvent(
      "AddPaymentInfo",
      {
        ...metaCommerceFromOrder(order, { value: extra.value }),
        ...extra,
      }
    );
  });
}

export function trackMetaPurchase(order, orderId = null, extra = {}) {
  const resolvedId = orderId ?? order?.id ?? order?.order_id ?? null;
  if (!resolvedId) return;
  oncePerSession(`purchase_${resolvedId}`, () => {
    trackMetaEvent(
      "Purchase",
      {
        ...metaCommerceFromOrder(order, { value: extra.value }),
        ...extra,
      },
      { eventId: `purchase_${resolvedId}` }
    );
  });
}

export function trackMetaLead(payload = {}) {
  const {
    leadType = "contact",
    content_name = null,
    value = null,
    dedupeKey = null,
  } = payload;
  const key = dedupeKey || `${leadType}_${content_name || "lead"}_${Date.now()}`;
  trackMetaEvent(
    "Lead",
    buildMetaCommerceParams({
      value,
      currency: "ILS",
      content_type: "lead",
      content_ids: [leadType],
      content_name: content_name || leadType,
    }),
    { eventId: `lead_${key}` }
  );
}
